import React, { createContext, ReactNode, useContext, useRef, useState } from "react";
import { Box, Divider, Stack } from "@mui/material";
import AiChatHistory from "../components/AiChat/AiChatHistory";
import { useCase } from "./CaseProvider";
import AiChatThreadMessages from "../components/AiChat/AiChatThreadMessages";
import AIChatInput from "../components/AiChat/AIChatInput";
import { getAiAgentByUser, sendAiChatPrompt } from "../services/aiChatService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource } from "../services/ReactQuery/reactQueryService";
import { useAiEventStreamControls } from "../hooks/useAiEventStream";
import CollapsableWindow, { CollapsableWindowProps } from "../components/CollapsableWindow/CollapsableWindow";
import { useCurrentUser } from "./CurrentUserProvider";
import FloatingAgentButton from "../components/AiChat/FloatingAgentButton";
import AiChatToolbar from "../components/AiChat/AiChatToolbar";
import { AiChatAgent } from "../types/AIChat/AiChatThread";
import { DrawerState } from "../types/DrawerState";
import { useOptionalDrawer } from "./DrawerProvider";

type AiChatProviderData = {
  openAiChat: (initialMessage: string) => void;
  isOpen: boolean;
  isLoading: boolean;
  setActiveThreadId: React.Dispatch<React.SetStateAction<number | null>>;
  setHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  activeThreadId: number | null;
  resetAiChat: (closeWindow?: boolean) => void;
  activeAgent: AiChatAgent;
  //execution step currently being edited in a drawer; the chat treats prompts as edit instructions for it
  activeEditStepId: number | null;
  openStepEditDrawer: (stepId: number, stepDrawerState: DrawerState) => void;
  closeStepEditDrawer: () => void;
};

const AiChatProviderContext = createContext<AiChatProviderData | null>(null);

export const AiChatProvider = ({ children }: { children: ReactNode }) => {
  const [windowState, setWindowState] = useState<CollapsableWindowProps["windowState"]>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<number | null>(null);
  const chatWrapperRef = useRef<HTMLDivElement>(null);
  const { product } = useCase();
  const currentUser = useCurrentUser();
  const createMutation = useMutation({
    mutationFn: createResource
  });
  const queryClient = useQueryClient();
  const { connect, reset } = useAiEventStreamControls();
  const drawer = useOptionalDrawer();
  const [editContext, setEditContext] = useState<{ stepId: number; drawerState: DrawerState } | null>(null);

  const openStepEditDrawer = (stepId: number, stepDrawerState: DrawerState) => {
    setEditContext({ stepId, drawerState: stepDrawerState });
    drawer?.setDrawerState(stepDrawerState);
  };

  //the edit context is only valid while its drawer is still the open one; derived, never synced
  const activeEditStepId = editContext && drawer?.drawerState === editContext.drawerState ? editContext.stepId : null;

  //closes the step-edit drawer only if it is still the open one (pending autosaves are flushed by useDraftAutosave on unmount)
  const closeStepEditDrawer = () => {
    if (editContext && drawer?.drawerState === editContext.drawerState) {
      drawer?.closeDrawer();
      setEditContext(null);
    }
  };

  //the drawer's live step data is scoped to the active thread, so switching or resetting the thread
  //closes an open step-edit drawer
  const changeActiveThread: AiChatProviderData["setActiveThreadId"] = (nextThreadId) => {
    closeStepEditDrawer();
    setActiveThreadId(nextThreadId);
  };

  const activeAgent = getAiAgentByUser(currentUser);

  const resetAiChat = (closeWindow = true) => {
    changeActiveThread(null);
    setHistoryOpen(false);
    setIsLoading(false);
    if (closeWindow) {
      setWindowState(null);
    }
  };

  if (!product) {
    return null;
  }

  if (!activeAgent) {
    return children;
  }

  const openAiChat = async (initialMessage?: string) => {
    setWindowState("maximized");
    if (initialMessage) {
      await sendAiChatPrompt(
        product,
        null,
        changeActiveThread,
        createMutation,
        queryClient,
        connect,
        reset,
        initialMessage,
        setIsLoading,
        activeAgent
      );
    }
  };

  const data: AiChatProviderData = {
    openAiChat,
    isOpen: ["maximized", "minimized"].includes(windowState || ""),
    setActiveThreadId: changeActiveThread,
    activeThreadId,
    setHistoryOpen,
    resetAiChat,
    setIsLoading,
    isLoading,
    activeAgent,
    activeEditStepId,
    openStepEditDrawer,
    closeStepEditDrawer,
  };

  return (
    <AiChatProviderContext.Provider value={data}>
      <CollapsableWindow windowState={windowState}>
        <AiChatToolbar windowState={windowState} setWindowState={setWindowState} />
        <Stack ref={chatWrapperRef} sx={{ flexGrow: 1, position: "relative", overflow: "hidden" }}>
          <Box sx={{ flexGrow: 1, padding: "1rem", overflowY: "auto" }}>
            <AiChatThreadMessages />
          </Box>
          <Divider />
          <AIChatInput isPanelExpanded={windowState === "maximized"} />
          <AiChatHistory
            open={historyOpen}
            closeHistory={() => setHistoryOpen(false)}
            chatWrapperRef={chatWrapperRef}
            backofficeCaseIri={product.backofficeCase["@id"]}
          />
        </Stack>
      </CollapsableWindow>
      <Box sx={{ position: "fixed", bottom: "3rem", right: "10rem" }}>
        <FloatingAgentButton disabled={!!windowState} onClick={() => setWindowState("maximized")} />
      </Box>
      {children}
    </AiChatProviderContext.Provider>
  );
};

export const useAiChat = () => {
  const context = useContext(AiChatProviderContext);
  if (!context) {
    throw new Error("useAiChat must be used within a AiChatProvider");
  }
  return context;
};

export const useOptionalAiChat = () => {
  return useContext(AiChatProviderContext) || null;
};
